// client/src/components/game_hud/HealthBar.jsx
// Итерация 5: полоска здоровья с зоной ран.
// Дизайн C: перекрытие — зелёная (здоровье) + красная (раны) + текст-процент.
//
// Логика:
//   current — текущий HP (0..max)
//   max     — максимум HP
//   wounds  — активные раны (закрывают часть полоски красным)
//
// Визуально:
//   [==== зелёный current ====][-- красный wounds --][-- пусто --]
//   |<------ max ------>|

import React from 'react';
import { Box, Typography, Tooltip, LinearProgress } from '@mui/material';
import { Favorite as FavoriteIcon } from '@mui/icons-material';

const HealthBar = ({ current = 100, max = 100, wounds = 0, isStale = false }) => {
  const safeMax = max > 0 ? max : 100;
  const safeCurrent = Math.max(0, Math.min(current, safeMax));
  const safeWounds = Math.max(0, Math.min(wounds, safeMax - safeCurrent));

  const currentPct = (safeCurrent / safeMax) * 100;
  const woundsPct = (safeWounds / safeMax) * 100;

  const getHealthColor = () => {
    if (currentPct > 80) return '#22c55e';
    if (currentPct > 50) return '#eab308';
    if (currentPct > 30) return '#f97316';
    return '#ef4444';
  };

  return (
    <Box sx={{ mb: 0.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FavoriteIcon sx={{ fontSize: 12, color: getHealthColor() }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', fontWeight: 600 }}>
            Здоровье
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.55rem',
            fontWeight: 700,
            color: isStale ? 'text.disabled' : getHealthColor(),
          }}
        >
          {Math.round(safeCurrent)}/{Math.round(safeMax)}
          {safeWounds > 0 && (
            <Box component="span" sx={{ color: '#ef4444', ml: 0.5 }}>
              (−{Math.round(safeWounds)})
            </Box>
          )}
        </Typography>
      </Box>

      <Tooltip
        title={
          safeWounds > 0
            ? `Здоровье: ${Math.round(safeCurrent)}/${Math.round(safeMax)} • Раны: ${Math.round(safeWounds)}`
            : `Здоровье: ${Math.round(safeCurrent)}/${Math.round(safeMax)}`
        }
        arrow
        placement="top"
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 4,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.08)',
            overflow: 'hidden',
            opacity: isStale ? 0.5 : 1,
            transition: 'opacity 0.3s ease',
          }}
        >
          {/* Зелёная зона — текущее HP */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${currentPct}%`,
              bgcolor: getHealthColor(),
              transition: 'width 0.6s ease, background-color 0.3s ease',
            }}
          />
          {/* Красная зона — раны (справа от current) */}
          {safeWounds > 0 && (
            <Box
              sx={{
                position: 'absolute',
                left: `${currentPct}%`,
                top: 0,
                bottom: 0,
                width: `${woundsPct}%`,
                bgcolor: '#ef4444',
                opacity: 0.7,
                transition: 'width 0.6s ease, left 0.6s ease',
                backgroundImage:
                  'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.2) 2px, rgba(0,0,0,0.2) 4px)',
              }}
            />
          )}
        </Box>
      </Tooltip>
    </Box>
  );
};

export default HealthBar;
